import React from 'react';
import { Card, CardContent } from './ui/card';
import { Avatar, AvatarImage, AvatarFallback } from './ui/avatar';
import { Star, Quote, MapPin, Building, ArrowRight } from 'lucide-react';
import { testimonials } from '../mock';

export const Testimonials = () => {
  return (
    <section className="py-20 bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 text-white relative overflow-hidden">
      {/* Background Elements */}
      <div className="absolute inset-0 opacity-10">
        <div className="absolute top-10 left-10 w-64 h-64 border border-purple-300 rounded-full"></div>
        <div className="absolute bottom-20 right-20 w-80 h-80 border border-orange-300 rounded-full"></div>
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-96 h-96 border border-yellow-300 rounded-full"></div>
      </div>
      
      <div className="container mx-auto px-4 relative z-10">
        {/* Header - Diagonal Layout */}
        <div className="grid lg:grid-cols-12 gap-8 mb-16">
          <div className="lg:col-span-6 lg:col-start-4 text-center space-y-6">
            <div className="inline-flex items-center space-x-2 bg-white/10 backdrop-blur-sm px-6 py-3 rounded-full border border-white/20">
              <Quote className="h-5 w-5 text-orange-300" />
              <span className="text-orange-100 font-semibold">Success Stories</span>
            </div>
            <h2 className="text-5xl font-black leading-tight">
              What Our <br />
              <span className="bg-gradient-to-r from-orange-400 to-yellow-400 bg-clip-text text-transparent">
                Students Say
              </span>
            </h2>
            <p className="text-xl text-purple-100 leading-relaxed">
              Hear from industry professionals who have transformed their safety knowledge 
              and advanced their careers through our specialized training programs.
            </p>
          </div>
        </div>

        {/* Testimonials - Creative Asymmetric Layout */}
        <div className="grid lg:grid-cols-12 gap-8">
          {/* Large Featured Testimonial */}
          <div className="lg:col-span-8">
            <Card className="border-0 bg-white/10 backdrop-blur-xl shadow-2xl hover:shadow-3xl transition-all duration-500 hover:scale-105 overflow-hidden">
              <CardContent className="p-8 lg:p-12">
                <div className="grid lg:grid-cols-12 gap-6 items-center">
                  <div className="lg:col-span-3">
                    <div className="relative">
                      <div className="absolute -inset-4 bg-gradient-to-r from-orange-400 to-yellow-400 rounded-full opacity-20"></div>
                      <Avatar className="h-24 w-24 lg:h-32 lg:w-32 mx-auto relative border-4 border-white/20">
                        <AvatarImage src={`https://ui-avatars.com/api/?name=${encodeURIComponent(testimonials[0].name)}&background=f97316&color=fff&bold=true&size=128`} />
                        <AvatarFallback className="bg-gradient-to-br from-orange-500 to-yellow-500 text-white text-2xl">
                          {testimonials[0].name.split(' ').map(n => n[0]).join('')}
                        </AvatarFallback>
                      </Avatar>
                    </div>
                  </div>
                  <div className="lg:col-span-9 space-y-6">
                    <div className="flex items-center space-x-1 mb-4">
                      {[...Array(testimonials[0].rating)].map((_, i) => (
                        <Star key={i} className="h-6 w-6 fill-yellow-400 text-yellow-400" />
                      ))}
                    </div>
                    
                    <blockquote className="relative">
                      <Quote className="h-12 w-12 text-orange-300 absolute -top-6 -left-2 opacity-50" />
                      <p className="text-xl lg:text-2xl leading-relaxed text-white font-medium pl-10 italic">
                        "{testimonials[0].content}"
                      </p>
                    </blockquote>
                    
                    <div className="border-t border-white/20 pt-6">
                      <div className="space-y-2">
                        <h4 className="text-2xl font-black text-white">{testimonials[0].name}</h4>
                        <p className="text-orange-300 font-semibold text-lg">{testimonials[0].position}</p>
                        <div className="flex items-center space-x-2 text-purple-200">
                          <Building className="h-4 w-4" />
                          <span className="text-sm">{testimonials[0].company}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Smaller Testimonials Stack */}
          <div className="lg:col-span-4 space-y-6">
            {testimonials.slice(1).map((testimonial, index) => {
              const gradients = [
                'from-blue-500 to-purple-600',
                'from-green-500 to-teal-600',
                'from-pink-500 to-red-600'
              ];
              
              return (
                <Card key={testimonial.id} className="border-0 bg-white/10 backdrop-blur-xl shadow-xl hover:shadow-2xl transition-all duration-300 hover:-translate-y-2 overflow-hidden group">
                  <div className={`h-1 bg-gradient-to-r ${gradients[index]}`}></div>
                  <CardContent className="p-6">
                    <div className="flex items-center space-x-1 mb-4">
                      {[...Array(testimonial.rating)].map((_, i) => (
                        <Star key={i} className="h-4 w-4 fill-yellow-400 text-yellow-400" />
                      ))}
                    </div>
                    
                    <blockquote className="mb-6">
                      <p className="text-white leading-relaxed italic text-sm">
                        "{testimonial.content}"
                      </p>
                    </blockquote>
                    
                    <div className="flex items-center space-x-4">
                      <Avatar className="h-12 w-12 border-2 border-white/20">
                        <AvatarImage src={`https://ui-avatars.com/api/?name=${encodeURIComponent(testimonial.name)}&background=${gradients[index].includes('blue') ? '3b82f6' : gradients[index].includes('green') ? '10b981' : 'ec4899'}&color=fff&bold=true&size=64`} />
                        <AvatarFallback className={`bg-gradient-to-br ${gradients[index]} text-white text-sm`}>
                          {testimonial.name.split(' ').map(n => n[0]).join('')}
                        </AvatarFallback>
                      </Avatar>
                      <div className="flex-1">
                        <h4 className="font-bold text-white text-lg group-hover:text-orange-300 transition-colors">
                          {testimonial.name}
                        </h4>
                        <p className="text-purple-200 text-sm">{testimonial.position}</p>
                        <div className="flex items-center space-x-1 text-purple-300 text-xs mt-1">
                          <Building className="h-3 w-3" />
                          <span>{testimonial.company}</span>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>

        {/* Call to Action - Floating Cards */}
        <div className="mt-20">
          <div className="grid lg:grid-cols-2 gap-8 items-center">
            <div className="space-y-8">
              <div className="space-y-4">
                <h3 className="text-4xl font-black text-white leading-tight">
                  Ready to Join Our <br />
                  <span className="bg-gradient-to-r from-orange-400 to-yellow-400 bg-clip-text text-transparent">
                    Success Stories?
                  </span>
                </h3>
                <p className="text-xl text-purple-100 leading-relaxed">
                  Transform your well control knowledge and advance your career in the oil & gas industry with our comprehensive training programs.
                </p>
              </div>
              
              <div className="flex flex-col sm:flex-row gap-4">
                <button 
                  onClick={() => document.getElementById('courses').scrollIntoView({ behavior: 'smooth' })}
                  className="bg-gradient-to-r from-orange-500 to-yellow-500 hover:from-orange-600 hover:to-yellow-600 text-white px-8 py-4 rounded-2xl font-bold text-lg transition-all hover:scale-105 hover:shadow-2xl shadow-orange-500/25 flex items-center justify-center"
                >
                  Browse Training Programs
                  <ArrowRight className="ml-2 h-5 w-5" />
                </button>
                <button 
                  onClick={() => document.getElementById('contact').scrollIntoView({ behavior: 'smooth' })}
                  className="border-2 border-purple-300 text-purple-100 hover:bg-purple-500 hover:text-white px-8 py-4 rounded-2xl font-bold text-lg transition-all hover:scale-105 bg-white/10 backdrop-blur-sm"
                >
                  Contact Our Team
                </button>
              </div>
            </div>

            <div className="relative">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-4">
                  <div className="bg-gradient-to-br from-orange-500 to-red-500 p-6 rounded-2xl text-white transform rotate-3 hover:rotate-0 transition-transform">
                    <div className="text-3xl font-black">500+</div>
                    <div className="text-orange-100">Professionals Trained</div>
                  </div>
                  <div className="bg-gradient-to-br from-blue-500 to-purple-500 p-6 rounded-2xl text-white transform -rotate-2 hover:rotate-0 transition-transform">
                    <div className="text-3xl font-black">95%</div>
                    <div className="text-blue-100">Success Rate</div>
                  </div>
                </div>
                <div className="space-y-4 mt-8">
                  <div className="bg-gradient-to-br from-green-500 to-teal-500 p-6 rounded-2xl text-white transform rotate-1 hover:rotate-0 transition-transform">
                    <div className="text-3xl font-black">6</div>
                    <div className="text-green-100">Specialized Courses</div>
                  </div>
                  <div className="bg-gradient-to-br from-yellow-500 to-orange-500 p-6 rounded-2xl text-white transform -rotate-1 hover:rotate-0 transition-transform">
                    <div className="text-3xl font-black">2 Years</div>
                    <div className="text-yellow-100">Certification Validity</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};